import { performSilentOidcAuthWithFallbackToRedirect } from '@diligentcorp/library-oidc-client-frontend-helpers';

import { HttpStatusCode, HttpErrorResponse, HttpConflictErrorResponse } from '../types/NetworkRequest';
import { BadRequestError } from '../api/http';
import AppConfig from '../AppConfig';

export async function handleResponse<T>(
  responseOrFn: Response | (() => Promise<Response>),
  onSuccess: (json: unknown | null) => T,
): Promise<T> {
  const response = await (typeof responseOrFn === 'function' ? responseOrFn() : responseOrFn);

  if (response.status === HttpStatusCode.FORBIDDEN || response.status === HttpStatusCode.UNAUTHORIZED) {
    const responseUrl = new URL(response.url);
    const relatedApiConfiguration = Object.values(AppConfig.proxyApiEndpoints).find(
      (api) =>
        responseUrl.pathname.startsWith(`${api.proxyPathPrefix}/`) || responseUrl.pathname === api.proxyPathPrefix,
    );

    if (relatedApiConfiguration) {
      await performSilentOidcAuthWithFallbackToRedirect(
        new URL(AppConfig.oidcAuthStartEndpointPath, window.location.origin),
        {
          resourceIndicator: relatedApiConfiguration.oidcResourceIndicator,
          postAuthRedirectPath: window.location.href.substring(window.location.origin.length),
          lockingStrategy: 'origin',
        },
      );

      if (typeof responseOrFn === 'function') {
        return await handleResponse(await responseOrFn(), onSuccess);
      }
    }

    let errorData;
    try {
      errorData = await response.clone().json();
    } catch {
      errorData = {};
    }
    throw new HttpErrorResponse(response, errorData.errors || []);
  }

  if (response.status === HttpStatusCode.BAD_REQUEST) {
    let errorData;
    try {
      errorData = await response.clone().json();
    } catch {
      errorData = {};
    }
    throw new BadRequestError(errorData.errors || []);
  }

  if (response.status === HttpStatusCode.CONFLICT) {
    let errorData;
    try {
      errorData = await response.clone().json();
    } catch {
      errorData = {};
    }
    throw new HttpConflictErrorResponse(response, errorData.errors || []);
  }

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.clone().json();
    } catch {
      errorData = {};
    }
    throw new HttpErrorResponse(response, errorData.errors || []);
  }

  if (response.status === HttpStatusCode.NO_CONTENT) {
    return onSuccess(null);
  }

  let data;
  try {
    data = (await response.json()) as unknown;
  } catch {
    data = null;
  }

  return onSuccess(data);
}
